/**
 * House rules and waits — the two settings behind the wishlist.
 *
 * The rules box is deliberately free text and deliberately the parent's own
 * words. Every family draws this line somewhere different, and an app that
 * offered a tidy set of toggles would be answering a question it hasn't got the
 * standing to answer. What it can do is put the family's sentence where a child
 * reads it at the moment they are deciding whether to ask.
 *
 * The waits underneath are the other half. They are a rule of the ship — the
 * same tiers apply to everybody, including the grown-up who typed them — which
 * is the whole reason a cooldown lands as "how buying works here" rather than
 * as a parent saying no.
 *
 * The toy rules at the bottom are the same idea aimed at the one category that
 * actually causes arguments. Both are optional and both start empty: a family
 * that has never had this problem should not meet a limit the app invented for
 * them, and a rule nobody asked for is a rule that gets switched off along with
 * everything near it.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useFamily } from '../lib/family.jsx';
import { BackLink } from '../ui/Nav.jsx';
import { setCooldownRule, setSpendingRule, useWishes } from '../lib/wishes.js';
import { HOUSE_RULES_DEFAULT } from '../lib/houseRulesDefault.js';
import { centsFromDollars, formatCents } from '../domain/money.js';

const dollars = (cents) => (cents / 100).toFixed(2);

/* A textarea is the only field in the product, so it borrows the tokens the
   shared input rule uses rather than earning a class of its own. */
const textareaStyle = {
  font: 'inherit',
  width: '100%',
  minHeight: '9rem',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--rule-strong)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  lineHeight: 'var(--leading-body)',
};

export default function HouseRules() {
  const { family, profile, setHouseRules } = useFamily();
  const wishes = useWishes(family?.id);
  // `null` means "whatever is saved", so the fields fill themselves in when the
  // rule finishes loading without an effect racing the typing.
  const [text, setText] = useState(null);
  const [rows, setRows] = useState(null);
  const [toyCap, setToyCap] = useState(null);
  const [toyDay, setToyDay] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  // What a household spends money on is a grown-up's decision, and a child who
  // could shorten their own cooldown has no cooldown.
  if (profile) return <Navigate to="/" replace />;

  // A family that has never written any rules gets the starter text in the
  // box rather than a blank one. A blank box asks a parent to invent a
  // spending philosophy from nothing, which is why most of them stay blank —
  // and a blank box means the child reads nothing at the moment they are
  // deciding whether to ask. The line under it says these are words to change.
  const unwritten = family?.house_rules == null;
  const value = text ?? family?.house_rules ?? HOUSE_RULES_DEFAULT;
  const tierRows = rows ?? wishes.rule.tiers.map((t) => ({
    over: dollars(t.overCents), days: String(t.waitDays),
  }));

  // What the domain will make of what has been typed, in the order it will
  // apply it. Sorted here for reading only — `waitDaysFor` sorts its own copy.
  const preview = tierRows
    .map((r) => ({
      overCents: centsFromDollars(r.over),
      waitDays: Math.max(0, Math.trunc(Number(r.days) || 0)),
    }))
    .sort((a, b) => a.overCents - b.overCents);

  const capValue = toyCap ?? (wishes.spending.toyCapCents == null
    ? '' : dollars(wishes.spending.toyCapCents));
  const dayValue = toyDay ?? (wishes.spending.toyDay == null
    ? '' : String(wishes.spending.toyDay));

  const editRow = (i, key, v) => setRows(tierRows.map((r, n) => (n === i ? { ...r, [key]: v } : r)));
  const addRow = () => setRows([...tierRows, { over: '', days: '1' }]);
  const dropRow = (i) => setRows(tierRows.filter((_, n) => n !== i));

  async function saveRules(e) {
    e.preventDefault();
    setBusy(true); setError(null); setSaved(null);
    try {
      await setHouseRules(value);
      setSaved('Saved. The kids see it on their list.');
    } catch (err) {
      setError(`That didn’t save — ${err.message}.`);
    }
    setBusy(false);
  }

  async function saveToys(e) {
    e.preventDefault();
    setBusy(true); setError(null); setSaved(null);
    try {
      await setSpendingRule({
        familyId: family.id,
        // Blank means no rule, and no rule is not the same as a rule of zero:
        // a $0.00 cap is a ban on toys, which is not what an empty field says.
        toyCapCents: capValue.trim() === '' ? null : centsFromDollars(capValue),
        toyDay: dayValue.trim() === '' ? null : Math.trunc(Number(dayValue)),
        effectiveFrom: wishes.today,
      });
      setToyCap(null); setToyDay(null);
      await wishes.reload();
      setSaved('Saved. Anything already asked for keeps the rule it was asked under.');
    } catch (err) {
      setError(`That didn’t save — ${err.message}.`);
    }
    setBusy(false);
  }

  async function saveWaits(e) {
    e.preventDefault();
    setBusy(true); setError(null); setSaved(null);
    try {
      await setCooldownRule({
        familyId: family.id,
        tiers: tierRows.map((r) => ({
          overCents: centsFromDollars(r.over),
          waitDays: Math.max(0, Math.trunc(Number(r.days) || 0)),
        })),
        effectiveFrom: wishes.today,
      });
      setRows(null);
      await wishes.reload();
      setSaved('Saved. Anything already waiting keeps the wait it was given.');
    } catch (err) {
      setError(`That didn’t save — ${err.message}.`);
    }
    setBusy(false);
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        <BackLink to="/wishes">Wishes</BackLink>
        <h1>House rules and waits</h1>
      </div>

      {wishes.error && <p className="notice error">{wishes.error}</p>}
      {error && <p className="notice error" role="alert">{error}</p>}
      {saved && <p className="notice info" role="status">{saved}</p>}

      <form className="card stack tight" onSubmit={saveRules}>
        <p className="eyebrow">How we spend</p>
        <p className="small muted">
          What your family spends money on here, and what it doesn’t. Your kids read this on
          their wishlist before they ask. Write it the way you’d say it.
        </p>
        <label>
          <span className="visually-hidden">House rules</span>
          <textarea
            style={textareaStyle}
            maxLength={2000}
            value={value}
            placeholder="We buy books any time. Sweets are a Friday thing. Anything with a screen in it, we talk about first."
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        {/* Only while nothing has been written. Once a family has their own
            words in here, telling them the words are a starter is noise. */}
        {unwritten && (
          <p className="small muted">
            These are starter words. Change them to yours — they are what your kids read.
          </p>
        )}
        <div className="row">
          <button className="primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save the rules'}
          </button>
          <button
            type="button"
            className="quiet"
            onClick={() => setText(HOUSE_RULES_DEFAULT)}
          >
            Reset to the starter text
          </button>
        </div>
      </form>

      <form className="card stack tight" onSubmit={saveWaits}>
        <p className="eyebrow">Waits</p>
        <p className="small muted">
          How long something sits before anyone can say yes. It is a rule of the ship — the same
          wait applies to everyone, grown-ups included — so it is not a no, it is how buying
          works here.
        </p>

        {tierRows.map((r, i) => (
          <div className="row" key={i}>
            <label style={{ flex: '1 1 8rem' }}>
              Over, in dollars
              <input
                type="number" inputMode="decimal" min="0" step="0.01" required
                value={r.over} onChange={(e) => editRow(i, 'over', e.target.value)}
              />
            </label>
            <label style={{ flex: '1 1 6rem' }}>
              Days to wait
              <input
                type="number" inputMode="numeric" min="0" step="1" required
                value={r.days} onChange={(e) => editRow(i, 'days', e.target.value)}
              />
            </label>
            <button type="button" className="quiet" onClick={() => dropRow(i)} disabled={tierRows.length <= 1}>
              Remove
            </button>
          </div>
        ))}

        {/* The lines read back in the order they will actually be applied,
            because the domain sorts them and a parent who types them out of
            order would otherwise be looking at a rule that isn't theirs. */}
        <ul className="stack tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {preview.map((t, i) => (
            <li className="small muted" key={i}>
              <span className="money">{formatCents(t.overCents)}</span> and up —{' '}
              {t.waitDays === 0
                ? 'no wait'
                : `${t.waitDays} ${t.waitDays === 1 ? 'day' : 'days'}`}
            </li>
          ))}
        </ul>

        <div className="row">
          <button className="primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save the waits'}
          </button>
          <button type="button" onClick={addRow}>Add a line</button>
        </div>
      </form>

      {/*
        Toys, which is where the arguments actually happen. Both fields are
        optional and both are blank by default: a family that has never had
        this problem should not meet a limit the app invented for them.
      */}
      <form className="card stack tight" onSubmit={saveToys}>
        <p className="eyebrow">Toys</p>

        <label>
          Monthly toy limit, in dollars
          <input
            type="number" inputMode="decimal" min="0" step="0.01" placeholder="Leave blank for none"
            value={capValue} onChange={(e) => setToyCap(e.target.value)}
          />
        </label>
        <p className="small muted">
          How much of their own money can go on toys in a month. Leave it blank for no limit.
        </p>

        <label>
          Toy day, a day of the month
          <input
            type="number" inputMode="numeric" min="1" max="28" step="1" placeholder="Leave blank for none"
            value={dayValue} onChange={(e) => setToyDay(e.target.value)}
          />
        </label>
        <p className="small muted">
          Toy wishes wait for the next one, like a monthly shopping trip. Leave it blank and
          toys go by the same waits as everything else.
        </p>

        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save the toy rules'}
        </button>
      </form>
    </main>
  );
}
